// Reusable "Size & Position" editor — width / height (px or %) + padding +
// margin (px). Used by every block in the Layout tab so size and position
// stay grouped and consistent. Padding and margin are intentionally
// co-located with size; both control the block's outer box, so editors
// shouldn't have to hunt across two sections to lay a block out.

import { useState } from 'react'
import type {
  Block, BlockDimension, BlockSize, BlockSpacing, BlockStyle, SizeUnit,
} from './types'
import { blockStyleLayer, setBlockStyleLayer } from './theme'
import { useViewport } from './ViewportContext'
import BreakpointHint from './BreakpointHint'

interface Props {
  block: Block
  onChange: (b: Block) => void
}

const FIELD_LABEL: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)',
  marginBottom: 6,
  display: 'block',
}

const MINI_LABEL: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginBottom: 4,
  display: 'block',
}

const TINY_LABEL: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
  marginTop: 2,
}

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.9125rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  color: 'var(--color-text)',
  outline: 'none',
}

const ROW: React.CSSProperties = { display: 'flex', gap: 6, alignItems: 'center' }

export default function SizeAndPositionEditor({ block, onChange }: Props) {
  // Padding & margin are per-breakpoint (they live on BlockStyle, which is
  // layered by the active device). Width / height stay global for now.
  const viewport = useViewport()
  const size: BlockSize = block.size || {}
  const style: BlockStyle = blockStyleLayer(block, viewport) || {}

  const setDim = (axis: 'width' | 'height', dim: BlockDimension | undefined) => {
    const next: BlockSize = { ...size }
    if (dim === undefined) delete next[axis]
    else next[axis] = dim
    const hasAny = next.width || next.height
    onChange({ ...block, size: hasAny ? next : undefined })
  }

  const patchStyle = (patch: Partial<BlockStyle>) => {
    const merged: BlockStyle = { ...style, ...patch }
    // Prune unset keys and keep the style while ANY key remains. A fixed
    // list of "meaningful" keys used to sit here, so clearing padding or
    // margin on a block whose only other style was hover / transition /
    // typography / zIndex / opacity / backdropBlur deleted all of it.
    for (const k of Object.keys(merged) as (keyof BlockStyle)[]) {
      if (merged[k] === undefined) delete merged[k]
    }
    onChange(setBlockStyleLayer(block, viewport, Object.keys(merged).length ? merged : undefined))
  }

  return (
    <div style={{
      borderTop: '1px solid var(--color-border)',
      paddingTop: 10, marginTop: 4,
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ ...FIELD_LABEL, marginBottom: 0 }}>Size &amp; Position</div>
      <BreakpointHint viewport={viewport} />

      {/* Width / Height */}
      <DimensionRow
        label="Width"
        dim={size.width}
        defaultUnit="%"
        onChange={(d) => setDim('width', d)}
      />
      <DimensionRow
        label="Height"
        dim={size.height}
        defaultUnit="px"
        onChange={(d) => setDim('height', d)}
      />

      {/* Padding (inside the block's box) */}
      <SpacingEditor
        label="Padding (px)"
        value={style.padding}
        onChange={(p) => patchStyle({ padding: p })}
      />

      {/* Margin (outside the block's box) */}
      <SpacingEditor
        label="Margin (px)"
        value={style.margin}
        onChange={(m) => patchStyle({ margin: m })}
      />

      <p style={{
        margin: 0, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.4,
      }}>
        Tip: drag the right / bottom edges or corner handle on the canvas to
        resize. Padding pushes content inward; margin pushes neighbours away.
      </p>
    </div>
  )
}

// ─── Width / Height row (number + unit) ─────────────────────

function DimensionRow({
  label,
  dim,
  defaultUnit,
  onChange,
}: {
  label: string
  dim: BlockDimension | undefined
  defaultUnit: SizeUnit
  onChange: (d: BlockDimension | undefined) => void
}) {
  const value = dim?.value
  const unit: SizeUnit = dim?.unit || defaultUnit
  return (
    <div>
      <label style={MINI_LABEL}>{label}</label>
      <div style={ROW}>
        <input
          type="number"
          min={0}
          value={value ?? ''}
          onChange={(e) => {
            const x = e.target.value === '' ? undefined : parseFloat(e.target.value)
            if (x === undefined || isNaN(x)) onChange(undefined)
            else onChange({ value: x, unit })
          }}
          placeholder="auto"
          style={{ ...INPUT, flex: 1 }}
        />
        <select
          value={unit}
          onChange={(e) => {
            if (value === undefined) return
            onChange({ value, unit: e.target.value as SizeUnit })
          }}
          style={{ ...INPUT, width: 72 }}
        >
          <option value="px">px</option>
          <option value="%">%</option>
        </select>
      </div>
    </div>
  )
}

// ─── Spacing (top/right/bottom/left, with "all-equal" mode) ──

export function SpacingEditor({
  label,
  value,
  onChange,
  placeholders,
}: {
  label: string
  value: BlockSpacing | undefined
  onChange: (next: BlockSpacing | undefined) => void
  /** Per-side hint for an empty cell (default "0") — e.g. "auto" where the
   *  renderer supplies its own value when the side is unset. */
  placeholders?: Partial<Record<keyof BlockSpacing, string>>
}) {
  const v = value || {}
  const allEqual = (
    v.top !== undefined && v.top === v.right && v.top === v.bottom && v.top === v.left
  )
  const [linked, setLinked] = useState(allEqual || (Object.keys(v).length === 0))

  const setSide = (side: keyof BlockSpacing, n: number | undefined) => {
    const next: BlockSpacing = { ...v, [side]: n }
    const empty = next.top === undefined && next.right === undefined
      && next.bottom === undefined && next.left === undefined
    onChange(empty ? undefined : next)
  }
  const setAll = (n: number | undefined) => {
    if (n === undefined) onChange(undefined)
    else onChange({ top: n, right: n, bottom: n, left: n })
  }
  const cellInput = (side: keyof BlockSpacing) => (
    // No `min` — both padding and margin accept negative values here.
    // CSS only renders negative margins (negative padding is clamped to
    // zero by the browser), but we let the user type either rather than
    // silently capping their input.
    <input
      type="number"
      value={v[side] ?? ''}
      onChange={(e) => {
        const x = e.target.value === '' ? undefined : parseInt(e.target.value, 10)
        setSide(side, typeof x === 'number' && !isNaN(x) ? x : undefined)
      }}
      placeholder={placeholders?.[side] ?? '0'}
      style={{ ...INPUT, padding: '4px 6px', textAlign: 'center' }}
    />
  )

  return (
    <div>
      <div style={{ ...ROW, justifyContent: 'space-between', marginBottom: 4 }}>
        <label style={{ ...MINI_LABEL, marginBottom: 0 }}>{label}</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          <input
            type="checkbox"
            checked={linked}
            onChange={(e) => {
              setLinked(e.target.checked)
              if (e.target.checked) {
                const n = v.top ?? v.right ?? v.bottom ?? v.left
                if (typeof n === 'number') setAll(n)
              }
            }}
          />
          Link sides
        </label>
      </div>
      {linked ? (
        <input
          type="number"
          value={v.top ?? ''}
          onChange={(e) => {
            const x = e.target.value === '' ? undefined : parseInt(e.target.value, 10)
            setAll(typeof x === 'number' && !isNaN(x) ? x : undefined)
          }}
          placeholder="0"
          style={INPUT}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
          <div>{cellInput('top')}<div style={TINY_LABEL}>top</div></div>
          <div>{cellInput('right')}<div style={TINY_LABEL}>right</div></div>
          <div>{cellInput('bottom')}<div style={TINY_LABEL}>bot</div></div>
          <div>{cellInput('left')}<div style={TINY_LABEL}>left</div></div>
        </div>
      )}
    </div>
  )
}
